import { useState } from 'react'
import DataTable from '../../components/DataTable'
import StatsCard from '../../components/StatsCard'
import { mockPartners } from '../../data/mockData'
import { Users2, ShoppingCart, Zap, TrendingUp } from 'lucide-react'

export default function PartnersPage() {
  const [sortKey, setSortKey] = useState('totalEarnings')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filterTier, setFilterTier] = useState('All')
  const [filterType, setFilterType] = useState('All')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  // Filter partners
  let filteredPartners = mockPartners
  if (filterTier !== 'All') {
    filteredPartners = filteredPartners.filter((p) => p.tier === filterTier)
  }
  if (filterType !== 'All') {
    filteredPartners = filteredPartners.filter((p) => p.type === filterType)
  }

  const activePartners = mockPartners.filter((p) => p.status === 'Active').length
  const totalCommissions = mockPartners.reduce((sum, p) => sum + p.totalEarnings, 0)
  const totalOffers = mockPartners.reduce((sum, p) => sum + p.offers, 0)
  const avgCommissionRate = (mockPartners.reduce((sum, p) => sum + p.commissionRate, 0) / mockPartners.length).toFixed(1)

  const columns = [
    { key: 'name', label: 'Business Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'location', label: 'Location', sortable: false },
    {
      key: 'tier',
      label: 'Tier',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Platinum' ? 'bg-blue-100 text-blue-700' :
          value === 'Gold' ? 'bg-yellow-100 text-yellow-700' :
          'bg-gray-100 text-gray-700'
        }`}>{value}</span>
      ),
    },
    { key: 'commissionRate', label: 'Commission %', sortable: true, render: (v) => `${v}%` },
    { key: 'offers', label: 'Offers', sortable: false },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
        }`}>{value}</span>
      ),
    },
    { key: 'totalEarnings', label: 'Earnings (AED)', sortable: true, render: (v) => `AED ${v.toLocaleString()}` },
  ]

  // Partner breakdown by type
  const partnerTypes = [...new Set(mockPartners.map(p => p.type))]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Partners</h1>
        <p className="text-pawssible-slate-600 mt-1">B2B partners, vets, and affiliate relationships</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Active Partners"
          value={activePartners}
          icon={Users2}
          color="teal"
        />
        <StatsCard
          label="Total Offers"
          value={totalOffers}
          icon={ShoppingCart}
          color="amber"
        />
        <StatsCard
          label="Avg Commission"
          value={`${avgCommissionRate}%`}
          icon={TrendingUp}
          color="teal"
        />
        <StatsCard
          label="Total Earnings"
          value={`AED ${(totalCommissions / 1000).toFixed(1)}k`}
          icon={Zap}
          color="amber"
        />
      </div>

      {/* Partner Type Breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Partner Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {partnerTypes.map((type) => {
            const count = mockPartners.filter((p) => p.type === type).length
            const earnings = mockPartners.filter((p) => p.type === type).reduce((sum, p) => sum + p.totalEarnings, 0)
            return (
              <div key={type} className="border border-pawssible-slate-200 rounded-lg p-4">
                <p className="font-semibold text-pawssible-slate-900">{type}</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-pawssible-slate-600">Count</span>
                    <span className="font-semibold text-pawssible-slate-900">{count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-pawssible-slate-600">Earnings</span>
                    <span className="font-semibold text-pawssible-slate-900">AED {earnings.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Filter Controls */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Filter by Tier</label>
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="input"
            >
              <option>All</option>
              <option>Platinum</option>
              <option>Gold</option>
              <option>Silver</option>
            </select>
          </div>
          <div>
            <label className="label">Filter by Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input"
            >
              <option>All</option>
              {partnerTypes.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Add Partner</button>
        <button className="btn-secondary">Generate Commission Report</button>
        <button className="btn-secondary">View Performance</button>
      </div>

      {/* Partners Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">All Partners</h2>
        <DataTable
          columns={columns}
          data={filteredPartners}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
        />
      </div>
    </div>
  )
}
